/**
 * Today (sales managers and admins): the office at a glance.
 *  - Day numbers: visitors (people), scheduled vs not, via broker, wait, meeting time
 *  - Team now: who is free / presenting
 *  - Reception: active visits first, compact completed history below
 *  - Today's scheduled visits, and the Needs action list (same items as My Today)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Timestamp, addDoc, collection, doc, getDoc, getDocs, limit, onSnapshot, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth, usePermissions } from '../AuthContext';
import {
  minutesSince,
  leaveReceptionVisit,
  receptionDepartureLabel,
  receptionSummary,
  returnReceptionVisit,
  takeReceptionVisit,
  timeLabel,
  toDate,
  useReceptionToday,
} from '../utils/reception';
import type { ReceptionDepartureReason, ReceptionVisit } from '../utils/reception';
import { dueText, isOverdue, needsActionNow, useMyTodayUrgent } from '../components/myTodayUrgent';
import FinishVisitDialog from '../components/FinishVisitDialog';
import LeadDetailDrawer from '../components/LeadDetailDrawer';
import PresenterBrief from '../components/PresenterBrief';
import LeaveReceptionDialog from '../components/LeaveReceptionDialog';
import IgnoreMenu from '../components/IgnoreMenu';
import type { UrgentItem } from '../components/myTodayUrgent';
import { displayName, IGNORE_REASONS, markItemDone, resolveReengagement } from '../utils/myTodayActions';
import { useConfirmAction } from '../components/ConfirmActionDialog';
import { openWhatsAppPanel } from '../utils/whatsappPanel';
import { grantPresenterDayAccess, revokePresenterDayAccess, useMyPresenterDayAccess, usePresenterDayGrants } from '../utils/presenterAccess';
import BrokerVisitPanel from '../components/BrokerVisitPanel';
import BrokerProfileInviteAction from '../components/BrokerProfileInviteAction';
import BrokerApprovalActions from '../components/BrokerApprovalActions';
import type { BrokerDoc } from '../components/BrokerDrawer';
import AddLeadDrawer from '../components/AddLeadDrawer';
import ReceptionSourceEditor from '../components/ReceptionSourceEditor';

// Needs action groups, most urgent first. `color` is the row's left edge.
const ACTION_GROUPS: Array<{ kind: UrgentItem['kind']; title: string; tab: string; color: string }> = [
  { kind: 'callback', title: 'Call-backs due', tab: 'Call-backs', color: '#b3261e' },
  { kind: 'human', title: 'Waiting for the team', tab: 'Waiting', color: '#e08a00' },
  { kind: 'reengaged', title: 'Came back after closing', tab: 'Came back', color: '#9d6f32' },
  { kind: 'visit', title: 'Visits not marked', tab: 'Visits not marked', color: '#b3261e' },
  { kind: 'visitMessageFailure', title: 'Visit WhatsApp messages failed', tab: 'Messages failed', color: '#b3261e' },
  { kind: 'visitTask', title: 'Calls to make', tab: 'Calls to make', color: '#2f6b9a' },
  { kind: 'visitRequest', title: 'Visit requests to schedule', tab: 'To schedule', color: '#2f6b9a' },
];
const PITCH_EDGE = '#c9c4ba';

// "12 min ago", "2h ago", "Yesterday".
const agoText = (d: Date | null, now: Date) => {
  if (!d) return '';
  const min = Math.max(0, Math.round((now.getTime() - d.getTime()) / 60000));
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  return h < 24 ? `${h}h ago` : h < 48 ? 'Yesterday' : `${Math.round(h / 24)}d ago`;
};

const GOLD = '#9d6f32';
const DARK = '#1c1b19';
const MUTED = '#5b5b5b';
const LINE = '#e8e3da';
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const CRM_PRESENTER_PAGES = new Set(['today', 'dashboard', 'leads', 'followUps', 'brokers', 'messages', 'metaLeads', 'reports']);

const COLUMNS: Array<{ key: ReceptionVisit['status']; title: string; color: string; bg: string; border: string }> = [
  { key: 'waiting', title: 'Waiting at reception', color: '#a35f00', bg: '#fff8ec', border: '#f0c98f' },
  { key: 'with', title: 'In meeting', color: '#2f6b9a', bg: '#eef4fa', border: '#c4d8ea' },
  { key: 'done', title: 'Done', color: '#2f6b35', bg: '#ffffff', border: LINE },
  { key: 'left', title: 'Left without meeting', color: MUTED, bg: '#ffffff', border: LINE },
];

const mins = (a: any, b: any) => {
  const x = toDate(a);
  const y = toDate(b);
  return x && y ? Math.max(0, Math.round((y.getTime() - x.getTime()) / 60000)) : null;
};

const avg = (values: number[]) => (values.length ? Math.round(values.reduce((s, v) => s + v, 0) / values.length) : null);

type BookedVisit = { id: string; name: string; at: Date; projectName: string; owner: string; status: string; customerConfirmed: boolean; runningLateAt: Date | null };

export default function TodayPage() {
  const { currentUser, roleId, currentProjectId } = useAuth();
  const { canAction } = usePermissions();
  const isAdmin = roleId === 'admin' || roleId === 'super-admin';
  const canRate = ['super-admin', 'admin', 'sales-manager'].includes(String(roleId || ''));
  const hasDayAccess = useMyPresenterDayAccess(db, currentUser?.uid, currentProjectId);
  const canPresent = canRate || hasDayAccess;
  const dayGrants = usePresenterDayGrants(db, canRate, currentProjectId);
  const [finishFor, setFinishFor] = useState<ReceptionVisit | null>(null);
  const [leaveFor, setLeaveFor] = useState<ReceptionVisit | null>(null);
  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [showFinished, setShowFinished] = useState(false);
  const [showAllDone, setShowAllDone] = useState(false);
  const [showAllLeft, setShowAllLeft] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<{ lead: any; open: boolean; visitLog: boolean }>({ lead: null, open: false, visitLog: false });
  const [actionError, setActionError] = useState<string | null>(null);
  const [ignoreFor, setIgnoreFor] = useState<{ anchor: HTMLElement; item: UrgentItem } | null>(null);
  const [visitOutcomeFor, setVisitOutcomeFor] = useState<{ anchor: HTMLElement; item: UrgentItem; outcome: 'no-show' | 'rescheduled' } | null>(null);
  const [visitRescheduleAt, setVisitRescheduleAt] = useState('');
  const [actionTab, setActionTab] = useState<UrgentItem['kind'] | 'all'>('all');
  const [legacyDiscussion, setLegacyDiscussion] = useState<Record<string, string[]>>({});
  const legacyDiscussionRequested = useRef(new Set<string>());

  const runAction = async (item: UrgentItem, fn: () => Promise<unknown>) => {
    setActionError(null);
    setBusyId(item.id);
    try {
      await fn();
    } catch (err: any) {
      setActionError(`Could not update ${displayName(item.name, item.phone10)}: ${err?.message || err}`);
    } finally {
      setBusyId(null);
    }
  };
  // Done, Reopen and Ignore were being tapped by mistake: each now asks once more.
  const [confirm, confirmDialog] = useConfirmAction();
  const subjectOf = (item: UrgentItem) => `${displayName(item.name, item.phone10)}${item.label ? `: ${item.label}` : ''}`;
  const confirmDone = async (item: UrgentItem) => {
    if (await confirm({ title: 'Mark this as done?', subject: subjectOf(item), body: 'It leaves the Today list.', confirmLabel: 'Yes, mark done' })) {
      runAction(item, () => markItemDone(db, item, myEmail || null));
    }
  };
  const confirmReopen = async (item: UrgentItem) => {
    const take = !item.ownerEmail;
    if (await confirm({ title: take ? 'Reopen this lead and take it?' : 'Reopen this lead?', subject: subjectOf(item), body: take ? 'The lead opens again and is assigned to you.' : 'The lead opens again for its owner.', confirmLabel: take ? 'Yes, reopen and take it' : 'Yes, reopen', tone: 'gold' })) {
      runAction(item, () => resolveReengagement(item.leadId as string, 'reopen', take));
    }
  };
  const confirmIgnore = async (item: UrgentItem, key: string) => {
    const reason = IGNORE_REASONS.find((r) => r.key === key)?.label || key;
    if (await confirm({ title: 'Ignore this message?', subject: subjectOf(item), body: `Reason: ${reason}.`, confirmLabel: 'Yes, ignore', tone: 'danger' })) {
      runAction(item, () => resolveReengagement(item.leadId as string, key));
    }
  };
  const saveVisitOutcome = async () => {
    const selection = visitOutcomeFor;
    const leadId = selection?.item.leadId;
    if (!selection || !leadId) return;
    const { item, outcome } = selection;
    setVisitOutcomeFor(null);
    setActionError(null);
    setBusyId(item.id);
    const leadRef = doc(db, 'leads', leadId);
    const by = myEmail || null;
    try {
      const snap = await getDoc(leadRef);
      const lead = (snap.data() || {}) as Record<string, any>;
      const appointment = lead.appointment || {};
      if (outcome === 'rescheduled') {
        const nextAt = new Date(visitRescheduleAt);
        if (!visitRescheduleAt || Number.isNaN(nextAt.getTime()) || nextAt.getTime() < Date.now()) {
          setActionError('Choose a future date and time to reschedule this visit.');
          return;
        }
        const scheduledAt = Timestamp.fromDate(nextAt);
        await updateDoc(leadRef, {
          appointment: { ...appointment, scheduledAt, status: 'scheduled', customerConfirmedAt: null, customerRunningLateAt: null, movedOnWhatsappAt: null, updatedAt: serverTimestamp(), updatedBy: by },
          nextFollowUpDate: scheduledAt,
          nextFollowUpAt: scheduledAt,
        });
        await addDoc(collection(db, 'leads', leadId, 'activities'), {
          type: 'internal', event: 'appointment-rescheduled', appointmentType: 'site-visit', appointmentAt: scheduledAt,
          appointmentOutcome: 'rescheduled', notes: ['Visit rescheduled from Today.'], byEmail: by, at: serverTimestamp(),
        });
      } else {
        await updateDoc(leadRef, {
          appointment: { ...appointment, status: 'no-show', outcome: 'no-show', completedAt: serverTimestamp(), updatedAt: serverTimestamp(), updatedBy: by },
        });
        await addDoc(collection(db, 'leads', leadId, 'activities'), {
          type: 'internal', event: 'appointment-completed', appointmentType: 'site-visit', appointmentAt: appointment.scheduledAt || null,
          appointmentOutcome: 'no-show', notes: ["Marked didn't come from Today."], byEmail: by, at: serverTimestamp(),
        });
      }
    } catch (err: any) {
      setActionError(`Could not update the visit: ${err?.message || err}`);
    } finally {
      setBusyId(null);
      setVisitRescheduleAt('');
    }
  };
  const navigate = useNavigate();
  const myEmail = String(currentUser?.email || '');
  const me = myEmail.toLowerCase();
  const [team, setTeam] = useState<boolean>(() => {
    try { return localStorage.getItem('crm:today:team') !== '0'; } catch { return true; }
  });
  // Individual sales users always see their own work. Managers and admins can
  // switch between Mine and Team.
  const teamView = canRate ? team : false;
  const [now, setNow] = useState(() => new Date());
  const [error, setError] = useState<string | null>(null);
  const [takingId, setTakingId] = useState<string | null>(null);
  const [users, setUsers] = useState<Array<{ uid: string; email: string; name: string; role: string; pageAccess: string[]; projectIds: string[] }>>([]);
  const [pendingBrokerApprovals, setPendingBrokerApprovals] = useState<BrokerDoc[]>([]);
  const [presenterMenu, setPresenterMenu] = useState<HTMLElement | null>(null);
  const [presenterSaving, setPresenterSaving] = useState<string | null>(null);
  const [addLeadFor, setAddLeadFor] = useState<UrgentItem | null>(null);
  const [booked, setBooked] = useState<BookedVisit[]>([]);
  // Service feedback after today's visits (WhatsApp: Excellent / Good / Could be better).
  const [scores, setScores] = useState<Array<{ score: string; presenter: string }>>([]);
  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'whatsappPostVisitJobs'), where('dayKey', '==', new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10))),
        (snap) => setScores(snap.docs.map((d) => d.data() as any).filter((j) => j.score).map((j) => ({ score: String(j.score), presenter: String(j.presenterEmail || '') }))),
        () => setScores([])
      ),
    []
  );

  useEffect(() => {
    if (!isAdmin) {
      setPendingBrokerApprovals([]);
      return;
    }
    return onSnapshot(collection(db, 'brokers'), (snap) => {
      setPendingBrokerApprovals(snap.docs
        .map((item) => ({id: item.id, ...(item.data() as any)} as BrokerDoc))
        .filter((broker) => broker.approvalStatus === 'pending')
        .sort((a, b) => (a.approvalRequestedAt?.toMillis?.() || 0) -
          (b.approvalRequestedAt?.toMillis?.() || 0)));
    }, () => setPendingBrokerApprovals([]));
  }, [isAdmin]);

  const reception = useReceptionToday(db);
  const receptionLeadIds = useMemo(
    () => Array.from(new Set(reception.map((visit) => visit.leadId).filter(Boolean))),
    [reception]
  );
  const [receptionLeadNames, setReceptionLeadNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!receptionLeadIds.length) {
      setReceptionLeadNames({});
      return undefined;
    }
    const activeIds = new Set(receptionLeadIds);
    setReceptionLeadNames((current) => Object.fromEntries(
      Object.entries(current).filter(([leadId]) => activeIds.has(leadId))
    ));
    const unsubscribers = receptionLeadIds.map((leadId) => onSnapshot(
      doc(db, 'leads', leadId),
      (snap) => {
        const name = String(snap.data()?.name || '').trim();
        setReceptionLeadNames((current) => ({ ...current, [leadId]: name }));
      },
      () => setReceptionLeadNames((current) => ({ ...current, [leadId]: '' }))
    ));
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [receptionLeadIds]);
  const urgentAll = useMyTodayUrgent(db, myEmail, true, teamView);

  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    try { localStorage.setItem('crm:today:team', team ? '1' : '0'); } catch { /* ignore */ }
    window.dispatchEvent(new Event('crm:today:scope-change'));
  }, [team]);

  useEffect(
    () =>
      onSnapshot(collection(db, 'users'), (snap) =>
        setUsers(
          snap.docs
            .map((d) => ({ ...(d.data() as any), uid: d.id }))
            .map((u) => ({
              uid: String(u.uid),
              email: String(u.email || '').toLowerCase(),
              name: String(u.displayName || u.name || u.email || ''),
              role: String(u.roleId || u.role || '').toLowerCase(),
              pageAccess: Array.isArray(u.pageAccess) ? u.pageAccess.filter((page: unknown): page is string => typeof page === 'string') : [],
              projectIds: Array.isArray(u.projectIds) ? u.projectIds.filter((project: unknown): project is string => typeof project === 'string') : [],
            }))
            .filter((u) => u.email)
            .sort((a, b) => a.name.localeCompare(b.name))
        )
      , () => setUsers([])),
    []
  );

  useEffect(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start.getTime() + 86400000);
    return onSnapshot(
      query(
        collection(db, 'leads'),
        where('appointment.scheduledAt', '>=', Timestamp.fromDate(start)),
        where('appointment.scheduledAt', '<', Timestamp.fromDate(end)),
        limit(300)
      ),
      (snap) =>
        setBooked(
          snap.docs
            .filter((d) => {
              const a = d.data().appointment || {};
              return !a.type || a.type === 'site-visit';
            })
            .map((d) => {
              const l = d.data();
              const a = l.appointment || {};
              return {
                id: d.id,
                name: String(l.name || 'Customer'),
                at: a.scheduledAt.toDate(),
                projectName: String(a.projectName || l.projectName || ''),
                owner: String(l.assignedTo || '').toLowerCase(),
                status: String(a.status || 'scheduled'),
                customerConfirmed: !!a.customerConfirmedAt,
                // Customer tapped "Running late" on the WhatsApp reminder.
                runningLateAt: toDate(a.customerRunningLateAt),
              };
            })
            .sort((x, y) => x.at.getTime() - y.at.getTime())
        ),
      () => setBooked([])
    );
  }, []);

  // Display name for an email; the email only when the user is unknown.
  const nameOf = (email: string | null) => users.find((u) => u.email === String(email || '').toLowerCase())?.name || email || '';
  const brokerAddedByName = (value: string | null | undefined) => {
    const recordedBy = String(value || '').trim();
    if (!recordedBy) return 'Front desk';
    if (!recordedBy.includes('@')) return recordedBy;
    const staffName = users.find((u) => u.email === recordedBy.toLowerCase())?.name.trim();
    return staffName && !staffName.includes('@') ? staffName : 'Team member';
  };

  const stats = useMemo(() => {
    const checked = reception;
    // Count people (leads), not check-ins: the same person checked in twice
    // today is one visitor; scheduled if any of their check-ins was.
    const people = new Map<string, { scheduled: boolean; broker: string | null }>();
    checked.forEach((r) => {
      const prev = people.get(r.leadId);
      people.set(r.leadId, {
        scheduled: (prev?.scheduled ?? false) || !!r.bookedAt,
        broker: prev?.broker || (String(r.sourceCorrection?.to?.sourceType || r.cameVia) === 'broker'
          ? String(r.sourceCorrection?.to?.brokerName || r.brokerName || 'Broker') : null),
      });
    });
    const scheduledCount = Array.from(people.values()).filter((p) => p.scheduled).length;
    const viaBroker = Array.from(people.values()).filter((p) => p.broker);
    const brokers = Array.from(new Set(viaBroker.map((p) => p.broker).filter(Boolean))) as string[];
    const waits = checked
      .map((r) => (r.status === 'waiting' ? minutesSince(r.checkedInAt, now) : mins(r.checkedInAt, r.takenAt || r.leftAt)))
      .filter((v): v is number => v !== null);
    const meetings = checked.filter((r) => r.status === 'done').map((r) => mins(r.takenAt, r.finishedAt)).filter((v): v is number => v !== null);
    const arrivedLeads = new Set(checked.map((r) => r.leadId));
    const expected = booked.filter((b) => b.status === 'scheduled' && !arrivedLeads.has(b.id));
    return {
      visitors: people.size,
      checkIns: checked.length,
      scheduledCount,
      unscheduled: people.size - scheduledCount,
      viaBroker: viaBroker.length,
      brokers,
      avgWait: avg(waits),
      longWaits: waits.filter((w) => w > 15).length,
      avgMeeting: avg(meetings),
      finished: meetings.length,
      expected,
    };
  }, [reception, booked, now]);

  // Regular presenters plus sales-team members approved for this project today.
  const teamNow = users.filter((u) => ['sales-manager', 'admin', 'super-admin'].includes(u.role) || dayGrants.some((grant) => grant.uid === u.uid)).map((u) => {
    const busy = reception.find((r) => r.status === 'with' && String(r.presenterEmail || '').toLowerCase() === u.email);
    return { ...u, busy, temporary: dayGrants.some((grant) => grant.uid === u.uid) };
  });
  const availableForDay = users.filter((u) =>
    ['sales-team', 'salesteam'].includes(u.role) &&
    u.pageAccess.some((page) => CRM_PRESENTER_PAGES.has(page)) &&
    !!currentProjectId && u.projectIds.includes(currentProjectId) &&
    !dayGrants.some((grant) => grant.uid === u.uid)
  );

  const scopedBooked = teamView ? booked : booked.filter((b) => b.owner === me || !b.owner);
  // Still to come: booked and not arrived. Checked-in ones live on the
  // reception board; finished ones go in the collapsed "Finished today".
  const receptionByLead = new Map(reception.map((r) => [r.leadId, r]));
  const stillToCome = scopedBooked.filter((b) => b.status === 'scheduled' && !receptionByLead.has(b.id));
  const finishedToday = scopedBooked.filter((b) => {
    const rv = receptionByLead.get(b.id);
    return b.status !== 'scheduled' || rv?.status === 'done' || rv?.status === 'left';
  });
  const finishedSummary = (() => {
    const done = finishedToday.filter((b) => b.status === 'completed' || receptionByLead.get(b.id)?.status === 'done').length;
    const noShow = finishedToday.filter((b) => b.status === 'no-show').length;
    const cancelled = finishedToday.filter((b) => b.status === 'cancelled').length;
    const left = finishedToday.filter((b) => b.status === 'scheduled' && receptionByLead.get(b.id)?.status === 'left').length;
    return [
      done ? `${done} done` : '',
      noShow ? `${noShow} didn't come` : '',
      cancelled ? `${cancelled} cancelled` : '',
      left ? `${left} left reception` : '',
    ].filter(Boolean).join(' · ');
  })();
  const needsAction = urgentAll.filter((u) => needsActionNow(u, now));
  const waitingVisits = reception.filter((visit) => visit.status === 'waiting');
  const meetingVisits = reception.filter((visit) => visit.status === 'with');
  const doneVisits = reception.filter((visit) => visit.status === 'done').sort((a, b) =>
    (toDate(b.finishedAt || b.takenAt || b.checkedInAt)?.getTime() || 0) - (toDate(a.finishedAt || a.takenAt || a.checkedInAt)?.getTime() || 0)
  );
  const leftVisits = reception.filter((visit) => visit.status === 'left').sort((a, b) =>
    (toDate(b.leftAt || b.checkedInAt)?.getTime() || 0) - (toDate(a.leftAt || a.checkedInAt)?.getTime() || 0)
  );

  // Older completed visits predate the notes copied onto receptionVisits.
  // Match their Visit activity to this meeting's time and presenter so a later
  // visit by the same lead cannot put its discussion on the wrong card.
  useEffect(() => {
    for (const visit of showAllDone ? doneVisits : doneVisits.slice(0, 3)) {
      if (visit.finalDiscussionNotes !== null || !visit.leadId || legacyDiscussionRequested.current.has(visit.id)) continue;
      const started = toDate(visit.takenAt)?.getTime();
      const finished = toDate(visit.finishedAt)?.getTime();
      if (!started || !finished) continue;
      legacyDiscussionRequested.current.add(visit.id);
      getDocs(query(collection(db, 'leads', visit.leadId, 'activities'), where('type', '==', 'visit')))
        .then((snap) => {
          const match = snap.docs.map((item) => item.data()).filter((activity) => {
            const at = toDate(activity.at)?.getTime();
            return at && at >= started - 60000 && at <= finished + 60000 &&
              String(activity.byEmail || '').toLowerCase() === String(visit.presenterEmail || '').toLowerCase();
          }).sort((a, b) => (toDate(b.at)?.getTime() || 0) - (toDate(a.at)?.getTime() || 0))[0];
          const notes = Array.isArray(match?.notes) ? match.notes.filter((note: unknown): note is string => typeof note === 'string' && !!note.trim()) : [];
          setLegacyDiscussion((current) => ({ ...current, [visit.id]: notes }));
        }).catch(() => { legacyDiscussionRequested.current.delete(visit.id); });
    }
  }, [reception, showAllDone]);

  const take = async (r: ReceptionVisit) => {
    if (!canPresent || (!canRate && r.projectId !== currentProjectId)) return;
    setError(null);
    setTakingId(r.id);
    try {
      await takeReceptionVisit(db, r, { email: myEmail, name: nameOf(myEmail) || currentUser?.displayName || myEmail });
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setTakingId(null);
    }
  };

  const returnToReception = async (r: ReceptionVisit) => {
    if (!await confirm({
      title: 'Return to reception?',
      subject: r.name,
      body: 'This removes the current presenter and puts the visitor back in Waiting at reception. The original check-in stays recorded.',
      confirmLabel: 'Return to reception',
      tone: 'gold',
    })) return;
    setError(null);
    setTakingId(r.id);
    try {
      await returnReceptionVisit(db, r, { email: myEmail, isAdmin });
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setTakingId(null);
    }
  };

  // Lead details open in a drawer on this page (no jump to Follow-ups).
  const openLead = async (leadId: string, visitLog = false) => {
    setActionError(null);
    try {
      const snap = await getDoc(doc(db, 'leads', leadId));
      if (!snap.exists()) {
        setActionError('That lead no longer exists.');
        return;
      }
      setDrawer({ lead: { id: snap.id, ...snap.data() }, open: true, visitLog });
    } catch (err: any) {
      setActionError(`Could not open the lead: ${err?.message || err}`);
    }
  };

  const card = { bgcolor: '#fff', border: `1px solid ${LINE}`, borderRadius: 1.5 } as const;
  const actBtn = {
    minHeight: 32, px: 1.5, borderRadius: 999, border: `1px solid ${LINE}`, bgcolor: '#fff', color: '#3d3d3d',
    fontFamily: FONT, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textDecoration: 'none',
    display: 'inline-flex', alignItems: 'center', '&:disabled': { opacity: 0.5, cursor: 'default' },
  } as const;
  // Needs action: compact square buttons, filter tabs and small grey pills.
  const rowBtn = {
    ...actBtn, minHeight: 30, px: 1.25, borderRadius: 2, borderColor: '#ddd6ca', color: '#1a1a1a', whiteSpace: 'nowrap',
    '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 2 },
  } as const;
  const priBtn = { ...rowBtn, bgcolor: GOLD, borderColor: GOLD, color: '#fff' } as const;
  const okBtn = { ...rowBtn, color: '#2f6b35', borderColor: '#cfe6d2' } as const;
  const tabSx = (on: boolean) => ({
    flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 0.75, minHeight: 30, px: 1.25,
    borderRadius: 999, border: `1px solid ${on ? DARK : LINE}`, bgcolor: on ? DARK : '#fff', color: on ? '#fff' : MUTED,
    fontFamily: FONT, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', scrollSnapAlign: 'start',
    '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 2 },
  });
  const greyPill = {
    px: 1, borderRadius: 999, bgcolor: '#f0efed', color: MUTED, fontFamily: FONT, fontSize: 11, fontWeight: 600, lineHeight: '18px', whiteSpace: 'nowrap',
  } as const;
  // Waiting-type items show how long ago; due-type items show due / overdue.
  const actionTime = (u: UrgentItem) =>
    u.kind === 'reengaged' || u.kind === 'human' ? agoText(u.dueAt, now)
      : u.kind === 'visitRequest' ? agoText(u.dueAt, now)
        : dueText(u, now);
  const btn = (bg: string) => ({
    minHeight: 36, px: 1.75, borderRadius: 999, border: 'none', bgcolor: bg, color: '#fff',
    fontFamily: FONT, fontWeight: 700, fontSize: 13, cursor: 'pointer',
  });

  const renderReceptionVisit = (r: ReceptionVisit, col: typeof COLUMNS[number]) => {
    const visitorName = receptionLeadNames[r.leadId] || r.name;
    const currentVisit = visitorName === r.name ? r : { ...r, name: visitorName };
    const mine = String(r.presenterEmail || '').toLowerCase() === me;
    const discussion = r.finalDiscussionNotes ?? legacyDiscussion[r.id] ?? [];
    const enteredBy = nameOf(r.checkedInBy) || r.checkedInBy || 'Front desk';
    const timelineColor = col.key === 'waiting' ? '#c47a18' : col.key === 'with' ? '#2f6b9a' : col.key === 'left' ? '#b3261e' : '#2f6b35';
    const timeline = col.key === 'waiting'
      ? [
          { label: 'Arrived', detail: `By ${enteredBy}`, meta: timeLabel(r.checkedInAt) },
          { label: 'Waiting at reception', detail: 'Ready for a presenter', meta: `${minutesSince(r.checkedInAt, now)} min` },
        ]
      : col.key === 'with'
        ? [
            { label: 'Arrived', detail: `By ${enteredBy}${r.takenAt ? ` · waited ${mins(r.checkedInAt, r.takenAt) ?? 0} min` : ''}`, meta: timeLabel(r.checkedInAt) },
            { label: 'Meeting started', detail: `${r.presenterName || r.presenterEmail || 'Team'} presenting`, meta: `${timeLabel(r.takenAt)} · ${minutesSince(r.takenAt, now)} min` },
          ]
        : col.key === 'left'
          ? [
              { label: 'Arrived', detail: `By ${enteredBy}`, meta: timeLabel(r.checkedInAt) },
              { label: 'Left without meeting', detail: `${receptionDepartureLabel(r.leftReason)}${r.leftNote ? ` · ${r.leftNote}` : ''}`, meta: `${timeLabel(r.leftAt)} · waited ${mins(r.checkedInAt, r.leftAt) ?? 0} min` },
            ]
          : [
              { label: 'Arrived', detail: `By ${enteredBy}${r.takenAt ? ` · waited ${mins(r.checkedInAt, r.takenAt) ?? 0} min` : ''}`, meta: timeLabel(r.checkedInAt) },
              { label: 'Meeting', detail: `${r.presenterName || r.presenterEmail || 'Team'} presented`, meta: `${timeLabel(r.takenAt)}${r.finishedAt ? ` · ${mins(r.takenAt, r.finishedAt) ?? 0} min` : ''}` },
              { label: 'Completed', detail: discussion.length ? discussion.join(' · ') : 'Visit completed', meta: timeLabel(r.finishedAt) },
            ];
    const right =
      col.key === 'waiting' ? `${minutesSince(r.checkedInAt, now)} min`
        : col.key === 'with' ? `${minutesSince(r.takenAt, now)} min`
          : col.key === 'done' ? `${mins(r.takenAt, r.finishedAt) ?? '—'} min`
            : `waited ${mins(r.checkedInAt, r.leftAt) ?? '—'} min`;
    const late = col.key === 'waiting' && minutesSince(r.checkedInAt, now) >= 10;
    return (
      <Box key={r.id} sx={{ bgcolor: '#fff', border: `1px solid ${col.border}`, borderRadius: 1.5, p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="button" type="button" onClick={() => openLead(r.leadId)}
            sx={{ flex: 1, minWidth: 0, textAlign: 'left', border: 'none', bgcolor: 'transparent', p: 0, cursor: 'pointer', fontFamily: FONT, fontWeight: 700, fontSize: 14.5, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {visitorName}
          </Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: late || col.key === 'left' ? '#b3261e' : col.color, flexShrink: 0 }}>{right}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#3d3d3d', flex: 1 }}>
            {receptionSummary(r)}{r.ownerEmail || r.ownerName ? ` · assigned to ${nameOf(r.ownerEmail) || r.ownerName}` : ''}
          </Typography>
          {isAdmin && (col.key === 'waiting' || col.key === 'with') &&
            <Box component="button" type="button" aria-label={`Edit visit source for ${visitorName}`}
              aria-expanded={editingSourceId === r.id} onClick={() => setEditingSourceId((id) => id === r.id ? null : r.id)}
              sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, flexShrink: 0, border: `1px solid ${LINE}`, borderRadius: '50%', bgcolor: '#fff', color: GOLD, cursor: 'pointer' }}>
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>}
        </Box>
        {isAdmin && editingSourceId === r.id && (col.key === 'waiting' || col.key === 'with') &&
          <ReceptionSourceEditor key={r.id} visit={r} byEmail={myEmail} onClose={() => setEditingSourceId(null)} />}
        {(col.key === 'waiting' || col.key === 'with') &&
          String(r.sourceCorrection?.to?.sourceType || r.cameVia) === 'broker' &&
          (r.sourceCorrection?.to?.brokerId || r.brokerId) &&
          <BrokerProfileInviteAction brokerId={String(r.sourceCorrection?.to?.brokerId || r.brokerId)}
            brokerName={String(r.sourceCorrection?.to?.brokerName || r.brokerName || 'Broker')} />}
        <Box aria-label="Visit timeline" sx={{ mt: 0.25, pt: 1, borderTop: `1px solid ${LINE}` }}>
          {timeline.map((step, index) => (
            <Box key={step.label} sx={{ display: 'grid', gridTemplateColumns: '16px minmax(0, 1fr)', columnGap: 1, minHeight: index < timeline.length - 1 ? 42 : 30 }}>
              <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: 9, height: 9, mt: 0.45, borderRadius: '50%', bgcolor: index === timeline.length - 1 ? timelineColor : '#8d9a91', border: `2px solid ${col.bg}`, zIndex: 1 }} />
                {index < timeline.length - 1 && <Box sx={{ position: 'absolute', top: 12, bottom: -3, width: 2, bgcolor: col.border }} />}
              </Box>
              <Box sx={{ minWidth: 0, pb: index < timeline.length - 1 ? 0.75 : 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700, color: index === timeline.length - 1 ? timelineColor : MUTED }}>{step.label}</Typography>
                  {step.meta && <Typography sx={{ fontFamily: FONT, fontSize: 10.5, color: MUTED, whiteSpace: 'nowrap' }}>{step.meta}</Typography>}
                </Box>
                <Typography title={step.detail} sx={{ fontFamily: FONT, fontSize: 12.25, color: '#30302e', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: step.label === 'Completed' || step.label === 'Left without meeting' ? 2 : 1, overflow: 'hidden', overflowWrap: 'anywhere' }}>
                  {step.detail}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
        {(col.key === 'waiting' || col.key === 'with') &&
          (isAdmin || col.key === 'waiting' || mine) && <PresenterBrief visit={currentVisit} />}
        {col.key === 'done' && isAdmin && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', pt: 0.25 }}>
            <Box component="button" type="button" onClick={() => openLead(r.leadId)} sx={{ ...rowBtn, minHeight: 28, fontSize: 11.5 }}>Open lead</Box>
            {r.phone10 && <Box component="button" type="button" onClick={() => openWhatsAppPanel({ phone10: r.phone10, name: visitorName, leadId: r.leadId })} sx={{ ...rowBtn, minHeight: 28, fontSize: 11.5 }}>Chat</Box>}
          </Box>
        )}
        {col.key === 'waiting' && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {(canRate || (hasDayAccess && r.projectId === currentProjectId)) && <Box component="button" type="button" disabled={takingId === r.id} onClick={() => take(currentVisit)} sx={btn(GOLD)}>
              {takingId === r.id ? '…' : "I'll take it"}
            </Box>}
            {isAdmin && <Box component="button" type="button" onClick={() => setLeaveFor(r)} sx={{ ...rowBtn, minHeight: 36, borderRadius: 999 }}>
              Visitor left
            </Box>}
          </Box>
        )}
        {col.key === 'with' && (mine || isAdmin) && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {mine && <Box component="button" type="button" onClick={() => setFinishFor(r)} sx={btn(DARK)}>Finish visit</Box>}
            <Box component="button" type="button" disabled={takingId === r.id} onClick={() => returnToReception(r)}
              sx={{ ...rowBtn, minHeight: 36, borderRadius: 999 }}>
              {takingId === r.id ? 'Returning…' : 'Return to reception'}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const statTiles = [
    {
      label: 'Visitors today',
      value: String(stats.visitors),
      sub: `${stats.scheduledCount} scheduled · ${stats.unscheduled} without schedule` +
        (stats.checkIns > stats.visitors ? ` · ${stats.checkIns} check-ins` : ''),
    },
    { label: 'Via broker', value: String(stats.viaBroker), sub: stats.brokers.slice(0, 2).join(', ') || '—' },
    { label: 'Average wait', value: stats.avgWait === null ? '—' : `${stats.avgWait} min`, sub: stats.longWaits ? `${stats.longWaits} waited over 15 min` : 'Nobody waited over 15 min', warn: stats.longWaits > 0 },
    { label: 'Average meeting', value: stats.avgMeeting === null ? '—' : `${stats.avgMeeting} min`, sub: `${stats.finished} finished` },
    (() => {
      const count = (s: string) => scores.filter((x) => x.score === s).length;
      const better = scores.filter((x) => x.score === 'better');
      const who = Array.from(new Set(better.map((x) => nameOf(x.presenter).split(/\s+/)[0]).filter(Boolean)));
      return {
        label: 'Service feedback',
        value: scores.length ? `⭐ ${count('excellent')} · 👍 ${count('good')} · 👎 ${count('better')}` : '—',
        sub: better.length ? `Could be better: ${who.join(', ') || 'see Messages'}` : scores.length ? 'No complaints today' : 'No answers yet today',
        warn: better.length > 0,
      };
    })(),
    { label: 'Still expected', value: String(stats.expected.length), sub: stats.expected.slice(0, 3).map((e) => timeLabel(e.at)).join(' · ') || '—' },
  ];

  const myVisitsToday = reception.filter((visit) => {
    const presenter = String(visit.presenterEmail || '').toLowerCase();
    const owner = String(visit.ownerEmail || '').toLowerCase();
    return presenter === me || (!presenter && owner === me);
  });
  const myVisitorCount = new Set(myVisitsToday.map((visit) => visit.leadId)).size;
  const myCompletedCount = reception.filter(
    (visit) => visit.status === 'done' && String(visit.presenterEmail || '').toLowerCase() === me
  ).length;
  const myScores = scores.filter((score) => String(score.presenter || '').toLowerCase() === me);
  const myScoreCount = (score: string) => myScores.filter((entry) => entry.score === score).length;
  const personalStatTiles = [
    { label: 'My visitors', value: String(myVisitorCount), sub: 'Assigned or presented today' },
    { label: 'Completed', value: String(myCompletedCount), sub: 'Meetings finished today' },
    { label: 'Upcoming', value: String(stillToCome.length), sub: stillToCome.length ? 'Scheduled visits remaining' : 'No visits remaining' },
    {
      label: 'My feedback',
      value: myScores.length ? `⭐ ${myScoreCount('excellent')} · 👍 ${myScoreCount('good')} · 👎 ${myScoreCount('better')}` : '—',
      sub: `${myScores.length} ${myScores.length === 1 ? 'response' : 'responses'} today`,
    },
  ];

  return (
    <Box sx={{ fontFamily: FONT, color: '#1a1a1a', maxWidth: 1360, mx: 'auto', px: { xs: 2, md: 3 }, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
        <Typography component="h1" sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 700 }}>Today</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 14, color: MUTED, flex: 1 }}>
          {now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
        </Typography>
        {canRate && <Box sx={{ display: 'flex', border: `1px solid ${LINE}`, borderRadius: 999, p: 0.4 }}>
          {([false, true] as const).map((t) => (
            <Box key={String(t)} component="button" type="button" onClick={() => setTeam(t)} aria-pressed={team === t}
              sx={{ minHeight: 32, px: 1.75, border: 'none', borderRadius: 999, cursor: 'pointer', fontFamily: FONT, fontSize: 13,
                fontWeight: team === t ? 700 : 500, bgcolor: team === t ? GOLD : 'transparent', color: team === t ? '#fff' : MUTED }}>
              {t ? 'Team' : 'Mine'}
            </Box>
          ))}
        </Box>}
      </Box>

      {error && <Typography role="alert" sx={{ fontFamily: FONT, color: '#b3261e', fontWeight: 600 }}>{error}</Typography>}

      {canRate && <Box component="section" aria-label="Day numbers" sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 1.5 }}>
        {statTiles.map((s) => (
          <Box key={s.label} sx={{ ...card, p: 2 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: MUTED }}>{s.label}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 28, fontWeight: 700, lineHeight: 1.3 }}>{s.value}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: s.warn ? '#b3261e' : MUTED }}>{s.sub}</Typography>
          </Box>
        ))}
      </Box>}

      {!canRate && <Box component="section" aria-label="My performance today" sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.25 }}>
        {personalStatTiles.map((stat) => (
          <Box key={stat.label} sx={{ ...card, p: 1.75 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: MUTED }}>{stat.label}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: stat.label === 'My feedback' ? 20 : 26, fontWeight: 700, lineHeight: 1.35 }}>{stat.value}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: MUTED }}>{stat.sub}</Typography>
          </Box>
        ))}
      </Box>}

      {(needsAction.length > 0 || pendingBrokerApprovals.length > 0) && (() => {
        // Likely sales pitches go below real customers.
        const groups = ACTION_GROUPS
          .map((g) => ({ ...g, items: needsAction.filter((u) => u.kind === g.kind).sort((a, b) => Number(!!a.possiblePitch) - Number(!!b.possiblePitch)) }))
          .filter((g) => g.items.length);
        // A tab whose items were all cleared falls back to All.
        const tab = groups.some((g) => g.kind === actionTab) ? actionTab : 'all';
        const shown = tab === 'all' ? groups : groups.filter((g) => g.kind === tab);
        return (
          <Box component="section" aria-label="Needs action" sx={{ ...card, p: { xs: 1.5, sm: 2 }, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography component="h2" sx={{ fontFamily: FONT, fontSize: 16, fontWeight: 700 }}>Needs action</Typography>
              <Box component="span" sx={{ px: 1, borderRadius: 999, bgcolor: DARK, color: '#fff', fontFamily: FONT, fontSize: 12, fontWeight: 700, lineHeight: '20px' }}>{needsAction.length + pendingBrokerApprovals.length}</Box>
            </Box>
            {groups.length > 1 && (
              // Wraps on desktop; one swipeable row on phones.
              <Box role="tablist" aria-label="Filter needs action" sx={{
                display: 'flex', gap: 0.75, flexWrap: { xs: 'nowrap', sm: 'wrap' }, overflowX: { xs: 'auto', sm: 'visible' },
                scrollSnapType: 'x proximity', mx: { xs: -1.5, sm: 0 }, px: { xs: 1.5, sm: 0 }, pb: { xs: 0.25, sm: 0 },
                scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
              }}>
                {[{ kind: 'all' as const, tab: 'All', count: needsAction.length }, ...groups.map((g) => ({ kind: g.kind, tab: g.tab, count: g.items.length }))].map((t) => (
                  <Box key={t.kind} component="button" type="button" role="tab" aria-selected={tab === t.kind} onClick={() => setActionTab(t.kind)} sx={tabSx(tab === t.kind)}>
                    {t.tab}
                    <Box component="span" sx={{ fontWeight: 700 }}>{t.count}</Box>
                  </Box>
                ))}
              </Box>
            )}
            {actionError && <Typography role="alert" sx={{ fontFamily: FONT, fontSize: 13, color: '#b3261e', fontWeight: 600 }}>{actionError}</Typography>}
            {pendingBrokerApprovals.length > 0 && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography sx={{ pl: 1.5, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: MUTED }}>
                New brokers to review · {pendingBrokerApprovals.length}
              </Typography>
              {pendingBrokerApprovals.map((broker) => <Box key={broker.id} sx={{
                display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) auto' },
                alignItems: 'center', gap: 1.25, py: 1.1, pl: 1.5, pr: 1.25,
                borderLeft: `3px solid ${GOLD}`, borderRadius: '0 8px 8px 0', bgcolor: '#fff8ed',
              }}>
                <Box sx={{minWidth: 0}}>
                  <Typography sx={{fontFamily: FONT, fontSize: 14, fontWeight: 700}}>{broker.brokerName || broker.displayName}</Typography>
                  <Typography sx={{fontFamily: FONT, fontSize: 12, color: MUTED}}>
                    {broker.phoneE164 || broker.phoneNational} · Added by {brokerAddedByName(broker.approvalRequestedBy)}
                  </Typography>
                </Box>
                <BrokerApprovalActions brokerId={broker.id} compact />
              </Box>)}
            </Box>}
            {shown.map((g) => (
              <Box key={g.kind} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography sx={{ pl: 1.5, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: MUTED }}>
                  {g.title} · {g.items.length}
                </Typography>
                {g.items.map((u) => {
                  const overdue = isOverdue(u, now);
                  const busy = busyId === u.id;
                  const pitch = !!u.possiblePitch;
                  const owner = u.ownerEmail ? nameOf(u.ownerEmail).split(/\s+/)[0] : 'Unassigned';
                  return (
                    <Box key={u.id} sx={{
                      display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) auto' }, alignItems: 'start', gap: { xs: 1, sm: 1.75 },
                      py: 1.1, pl: 1.5, pr: 1.25, borderLeft: `3px solid ${pitch ? PITCH_EDGE : g.color}`, borderRadius: '0 8px 8px 0',
                      bgcolor: '#fff', opacity: pitch ? 0.8 : 1, '&:hover': { bgcolor: '#fcfaf6', opacity: 1 },
                    }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: u.kind === 'human' ? 'column' : 'row', alignItems: u.kind === 'human' ? 'flex-start' : 'baseline', gap: u.kind === 'human' ? 0.25 : 1, flexWrap: 'wrap', minWidth: 0 }}>
                          <Box component="button" type="button" onClick={() => u.leadId && openLead(u.leadId)}
                            sx={{ border: 'none', bgcolor: 'transparent', p: 0, cursor: 'pointer', fontFamily: FONT, fontSize: 14, fontWeight: 700, color: '#1a1a1a', textAlign: 'left', '&:hover': { textDecoration: 'underline' } }}>
                            {displayName(u.name, u.phone10)}
                          </Box>
                          {u.kind === 'human' && u.phone10 && (
                            <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                              +91 {u.phone10.slice(0, 5)} {u.phone10.slice(5)}
                            </Typography>
                          )}
                          <Typography sx={{
                            fontFamily: FONT, fontSize: 13, color: pitch ? MUTED : '#3d3d3d', fontStyle: pitch ? 'italic' : 'normal', flex: u.kind === 'human' ? '0 0 auto' : '1 1 160px', minWidth: 0,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: { xs: 'normal', sm: 'nowrap' },
                            display: u.kind === 'human' ? 'block' : { xs: '-webkit-box', sm: 'block' }, WebkitLineClamp: u.kind === 'human' ? 'unset' : 2, WebkitBoxOrient: 'vertical',
                          }}>
                            {u.label}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography component="span" sx={{ fontFamily: FONT, fontSize: 12, fontWeight: overdue ? 700 : 500, color: overdue ? '#b3261e' : MUTED, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {actionTime(u)}
                          </Typography>
                          <Typography component="span" sx={{ fontFamily: FONT, fontSize: 12, color: u.ownerEmail ? MUTED : '#856404', whiteSpace: 'nowrap' }}>· {owner}</Typography>
                          {u.closeInfo && <Box component="span" sx={greyPill}>{u.closeInfo.replace(/\s*\((.+)\)$/, ' · $1')}</Box>}
                          {pitch && <Box component="span" sx={{ ...greyPill, bgcolor: '#fff3e0', color: '#a35f00' }}>Likely sales pitch</Box>}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        {u.kind === 'reengaged' && u.leadId && (
                          <>
                            {/* A likely pitch makes Ignore the main button. */}
                            <Box component="button" type="button" disabled={busy} onClick={() => confirmReopen(u)}
                              sx={pitch ? rowBtn : priBtn}>
                              {u.ownerEmail ? 'Reopen' : 'Reopen & take it'}
                            </Box>
                            <Box component="button" type="button" disabled={busy} onClick={(e) => setIgnoreFor({ anchor: e.currentTarget, item: u })} sx={pitch ? priBtn : rowBtn}>
                              Ignore ▾
                            </Box>
                          </>
                        )}
                        {(u.kind === 'callback' || u.kind === 'human' || u.kind === 'visitTask' || u.kind === 'visitMessageFailure') && (
                          <>
                            {u.phone10 && <Box component="a" href={`tel:+91${u.phone10}`} sx={rowBtn}>Call</Box>}
                            {u.phone10 && (
                              <Box component="button" type="button" onClick={() => openWhatsAppPanel({ phone10: u.phone10, name: u.name, leadId: u.leadId })} sx={rowBtn}>
                                Chat
                              </Box>
                            )}
                            {u.kind === 'human' && u.leadLookupComplete && !u.leadId && u.phone10 && canAction('leads', 'addLead') && (
                              <Box component="button" type="button" onClick={() => setAddLeadFor(u)} sx={priBtn}>
                                Add to leads
                              </Box>
                            )}
                            <Box component="button" type="button" disabled={busy} onClick={() => confirmDone(u)} sx={okBtn}>
                              Done
                            </Box>
                          </>
                        )}
                        {u.kind === 'visitRequest' && u.leadId && (
                          <Box component="button" type="button" onClick={() => openLead(u.leadId as string)} sx={rowBtn}>
                            Schedule visit
                          </Box>
                        )}
                        {u.kind === 'visit' && u.visitDay === 'past' && u.leadId && (
                          <>
                            <Box component="button" type="button" disabled={busy} onClick={(e) => setVisitOutcomeFor({ anchor: e.currentTarget, item: u, outcome: 'no-show' })}
                              sx={{ ...rowBtn, color: '#b3261e', borderColor: '#f1c9c9' }}>
                              Didn’t come
                            </Box>
                            <Box component="button" type="button" disabled={busy} onClick={(e) => { setVisitRescheduleAt(''); setVisitOutcomeFor({ anchor: e.currentTarget, item: u, outcome: 'rescheduled' }); }}
                              sx={{ ...rowBtn, color: GOLD, borderColor: `${GOLD}66` }}>
                              Reschedule
                            </Box>
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
            <IgnoreMenu
              anchorEl={ignoreFor?.anchor || null}
              onClose={() => setIgnoreFor(null)}
              suggestPitch={!!ignoreFor?.item.possiblePitch}
              onPick={(key) => {
                const item = ignoreFor?.item;
                setIgnoreFor(null);
                if (item?.leadId) confirmIgnore(item, key);
              }}
            />
            <Menu anchorEl={visitOutcomeFor?.anchor || null} open={!!visitOutcomeFor} onClose={() => setVisitOutcomeFor(null)}>
              {visitOutcomeFor && <Box sx={{ px: 2, py: 1, width: { xs: 260, sm: 290 }, maxWidth: 'calc(100vw - 48px)' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: DARK, mb: 0.5 }}>
                  {visitOutcomeFor.outcome === 'no-show' ? `${displayName(visitOutcomeFor.item.name, visitOutcomeFor.item.phone10)} didn’t come?` : `Reschedule ${displayName(visitOutcomeFor.item.name, visitOutcomeFor.item.phone10)}`}
                </Typography>
                {visitOutcomeFor.outcome === 'rescheduled' && <Box component="input" type="datetime-local" value={visitRescheduleAt}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVisitRescheduleAt(e.target.value)}
                  sx={{ fontFamily: FONT, fontSize: 13, width: '100%', p: 0.75, mb: 0.75, border: `1px solid ${LINE}`, borderRadius: 1, boxSizing: 'border-box' }} />}
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: MUTED, mb: 1 }}>
                  {visitOutcomeFor.outcome === 'no-show' ? 'Mark this scheduled visit as missed.' : 'Choose a new future date and time for this visit.'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                  <Box component="button" type="button" onClick={() => setVisitOutcomeFor(null)} sx={rowBtn}>Back</Box>
                  <Box component="button" type="button" disabled={busyId === visitOutcomeFor.item.id || (visitOutcomeFor.outcome === 'rescheduled' && !visitRescheduleAt)} onClick={saveVisitOutcome}
                    sx={{ ...rowBtn, color: '#fff', bgcolor: visitOutcomeFor.outcome === 'no-show' ? '#b3261e' : GOLD, borderColor: 'transparent', '&:disabled': { opacity: 0.5 } }}>
                    {busyId === visitOutcomeFor.item.id ? 'Saving…' : visitOutcomeFor.outcome === 'no-show' ? 'Mark didn’t come' : 'Save new time'}
                  </Box>
                </Box>
              </Box>}
            </Menu>
          </Box>
        );
      })()}

      {stillToCome.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'start' }}>
        <Box component="section" aria-label="Today's scheduled visits" sx={{ ...card, p: 2, flex: '1 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography component="h2" sx={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, mb: 0.5 }}>Today's scheduled visits</Typography>
          {[...stillToCome, ...(showFinished ? finishedToday : [])].map((b, i, list) => {
            const rv = receptionByLead.get(b.id);
            const finished = i >= stillToCome.length;
            const state: { text: string; color: string; bg?: string } =
              b.status === 'completed' ? { text: 'Done', color: '#2f6b35' }
                : b.status === 'no-show' ? { text: "Didn't come", color: '#b3261e' }
                  : b.status === 'cancelled' ? { text: 'Cancelled', color: MUTED }
                    : rv?.status === 'done' ? { text: 'Done', color: '#2f6b35' }
                      : rv?.status === 'left' ? { text: 'Left reception', color: '#b3261e' }
                        : b.runningLateAt ? { text: `Running late · told ${timeLabel(b.runningLateAt)}`, color: '#a35f00', bg: '#fff3e0' }
                          : b.customerConfirmed ? { text: 'Confirmed on WhatsApp', color: MUTED }
                            : { text: 'Expected', color: MUTED };
            return (
              <React.Fragment key={b.id}>
                {i === stillToCome.length && finishedToday.length > 0 && (
                  <Box sx={{ height: 1, bgcolor: '#e8e3da', my: 0.5 }} />
                )}
                <Box component="button" type="button" onClick={() => openLead(b.id)}
                  sx={{ display: 'grid', gridTemplateColumns: '80px minmax(0, 1fr) auto', gap: 1.25, alignItems: 'center', textAlign: 'left',
                    py: 1, border: 'none', borderBottom: i < list.length - 1 && i !== stillToCome.length - 1 ? '1px solid #f0ece4' : 'none', bgcolor: 'transparent', cursor: 'pointer',
                    fontFamily: FONT, fontSize: 13.5, color: '#1a1a1a', opacity: finished ? 0.6 : 1 }}>
                  <span style={{ fontWeight: 700 }}>{timeLabel(b.at)}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {b.name}{b.projectName ? ` · ${b.projectName}` : ''}{b.owner ? ` · ${nameOf(b.owner).split(/\s+/)[0]}` : ' · Unassigned'}
                  </span>
                  <span style={{
                    fontSize: 12, fontWeight: 600, color: state.color, whiteSpace: 'nowrap',
                    ...(state.bg ? { background: state.bg, borderRadius: 999, padding: '2px 8px' } : {}),
                  }}>
                    {state.text}
                  </span>
                </Box>
              </React.Fragment>
            );
          })}
          {finishedToday.length > 0 && (
            <Box component="button" type="button" onClick={() => setShowFinished((v) => !v)} aria-expanded={showFinished}
              sx={{ mt: 0.5, alignSelf: 'flex-start', border: 'none', bgcolor: 'transparent', p: 0.5, cursor: 'pointer', fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: MUTED }}>
              {showFinished ? '▾' : '▸'} Finished today ({finishedToday.length}){finishedSummary ? `: ${finishedSummary}` : ''}
            </Box>
          )}
        </Box>

      </Box>}

      {canRate && <Box component="section" aria-label="Team now" sx={{ ...card, p: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, mr: 0.5 }}>Team now</Typography>
        {teamNow.map((u) => (
          <Box key={u.email} sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 34, px: 1.5, borderRadius: 999, fontSize: 13, fontFamily: FONT,
            bgcolor: u.busy ? '#eef4fa' : '#f2f9f3', border: `1px solid ${u.busy ? '#c4d8ea' : '#cfe6d2'}`,
          }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: u.busy ? '#2f6b9a' : '#2f6b35' }} />
            <b>{u.name.split(/\s+/)[0]}</b>
            {u.busy ? `With ${u.busy.name.split(/\s+/)[0]} · ${minutesSince(u.busy.takenAt, now)} min` : u.temporary ? 'Presenter today' : 'Free'}
            {u.temporary && canRate && <Box component="button" type="button" aria-label={`Remove ${u.name}'s presenter access today`}
              disabled={presenterSaving === u.uid}
              onClick={async () => {
                if (!currentProjectId) return;
                setPresenterSaving(u.uid); setError(null);
                try { await revokePresenterDayAccess(db, currentProjectId, u.uid, myEmail); }
                catch (err: any) { setError(err?.message || String(err)); }
                finally { setPresenterSaving(null); }
              }}
              sx={{ border: 0, bgcolor: 'transparent', cursor: 'pointer', fontSize: 15, lineHeight: 1, ml: 0.25 }}>×</Box>}
          </Box>
        ))}
        {canRate && currentProjectId && <>
          <Box component="button" type="button" aria-label="Add presenter for today" title="Allow a sales team member to present today"
            onClick={(event: React.MouseEvent<HTMLElement>) => setPresenterMenu(event.currentTarget)}
            sx={{ width: 34, height: 34, borderRadius: '50%', border: `1px solid ${LINE}`, bgcolor: '#fff', color: GOLD, fontSize: 22, cursor: 'pointer' }}>+</Box>
          <Menu anchorEl={presenterMenu} open={!!presenterMenu} onClose={() => setPresenterMenu(null)}>
            <Typography sx={{ px: 2, py: 1, fontSize: 12, color: MUTED }}>Present for this project today</Typography>
            {availableForDay.length === 0 && <MenuItem disabled>No sales team members to add</MenuItem>}
            {availableForDay.map((user) => <MenuItem key={user.uid} disabled={!!presenterSaving}
              onClick={async () => {
                setPresenterMenu(null); setPresenterSaving(user.uid); setError(null);
                try { await grantPresenterDayAccess(db, currentProjectId, user, myEmail); }
                catch (err: any) { setError(err?.message || String(err)); }
                finally { setPresenterSaving(null); }
              }}>{user.name}</MenuItem>)}
          </Menu>
        </>}
      </Box>}

      {waitingVisits.length > 0 && <Box component="section" aria-label="Reception" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography component="h2" sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 700 }}>Reception</Typography>
          <Typography sx={{ px: 1.25, py: 0.4, borderRadius: 999, bgcolor: '#fff3df', color: '#a35f00', fontFamily: FONT, fontSize: 12, fontWeight: 700 }}>Waiting {waitingVisits.length}</Typography>
        </Box>
        <Box sx={{ bgcolor: COLUMNS[0].bg, border: `1px solid ${COLUMNS[0].border}`, borderLeft: '4px solid #d68b25', borderRadius: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: COLUMNS[0].color }}>Waiting at reception ({waitingVisits.length})</Typography>
          {waitingVisits.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED }}>Nobody is waiting.</Typography>}
          {waitingVisits.map((visit) => renderReceptionVisit(visit, COLUMNS[0]))}
        </Box>
      </Box>}

      {meetingVisits.length > 0 && <Box component="section" aria-label="Meetings in progress" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography component="h2" sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 700 }}>Meetings in progress</Typography>
          <Typography sx={{ px: 1.25, py: 0.4, borderRadius: 999, bgcolor: '#e7f1fa', color: '#2f6b9a', fontFamily: FONT, fontSize: 12, fontWeight: 700 }}>Active {meetingVisits.length}</Typography>
        </Box>
        <Box sx={{ bgcolor: COLUMNS[1].bg, border: `1px solid ${COLUMNS[1].border}`, borderLeft: '4px solid #4d83ad', borderRadius: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: COLUMNS[1].color }}>With the sales team ({meetingVisits.length})</Typography>
          {meetingVisits.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED }}>No meetings are in progress.</Typography>}
          {meetingVisits.map((visit) => renderReceptionVisit(visit, COLUMNS[1]))}
        </Box>
      </Box>}

      <Box component="section" aria-label="Today's visit outcomes" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography component="h2" sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 700 }}>Today’s visit outcomes</Typography>

        {leftVisits.length > 0 && <Box component="section" aria-label="Left without meeting" sx={{ ...card, p: 2, borderColor: '#e6c6c1' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
            <Typography component="h3" sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: '#a3352a' }}>Left without meeting ({leftVisits.length})</Typography>
            {leftVisits.length > 3 && <Box component="button" type="button" onClick={() => setShowAllLeft((value) => !value)} aria-expanded={showAllLeft} sx={{ border: 'none', bgcolor: 'transparent', color: GOLD, fontFamily: FONT, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>{showAllLeft ? 'Show latest 3' : `View all (${leftVisits.length})`}</Box>}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25, maxHeight: showAllLeft ? 440 : 'none', overflowY: showAllLeft ? 'auto' : 'visible' }}>
            {(showAllLeft ? leftVisits : leftVisits.slice(0, 3)).map((visit) => renderReceptionVisit(visit, COLUMNS[3]))}
          </Box>
        </Box>}

        <Box component="section" aria-label="Completed visits" sx={{ ...card, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: doneVisits.length ? 1.25 : 0 }}>
            <Typography component="h3" sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: '#2f6b35' }}>Completed meetings ({doneVisits.length})</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {!leftVisits.length && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED }}>Left without meeting (0)</Typography>}
              {doneVisits.length > 3 && <Box component="button" type="button" onClick={() => setShowAllDone((value) => !value)} aria-expanded={showAllDone} sx={{ border: 'none', bgcolor: 'transparent', color: GOLD, fontFamily: FONT, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>{showAllDone ? 'Show latest 3' : `View all done (${doneVisits.length})`}</Box>}
            </Box>
          </Box>
          {doneVisits.length === 0 ? <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED }}>No completed visits yet.</Typography> :
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25, maxHeight: showAllDone ? 440 : 'none', overflowY: showAllDone ? 'auto' : 'visible' }}>
              {(showAllDone ? doneVisits : doneVisits.slice(0, 3)).map((visit) => renderReceptionVisit(visit, COLUMNS[2]))}
            </Box>}
        </Box>
      </Box>

      <BrokerVisitPanel mode="team" />



      <FinishVisitDialog
        open={!!finishFor}
        leadId={finishFor?.leadId || null}
        visit={finishFor ? reception.find((r) => r.id === finishFor.id) || finishFor : null}
        canRate={canRate}
        onClose={() => setFinishFor(null)}
        onOpenLead={(id) => { setFinishFor(null); openLead(id, true); }}
      />

      <LeaveReceptionDialog visit={leaveFor} onClose={() => setLeaveFor(null)}
        onSave={(visit: ReceptionVisit, reason: ReceptionDepartureReason, note: string) =>
          leaveReceptionVisit(db, visit, { reason, note, byEmail: myEmail })} />

      {confirmDialog}

      <AddLeadDrawer
        open={!!addLeadFor}
        onClose={() => setAddLeadFor(null)}
        onCreated={() => setAddLeadFor(null)}
        prefill={addLeadFor ? {
          phone: `+91${addLeadFor.phone10}`,
          name: /^\+?[\d\s()-]+$/.test(String(addLeadFor.name || '').trim()) ? '' : addLeadFor.name,
          source: 'WhatsApp',
        } : undefined}
      />

      <LeadDetailDrawer
        open={drawer.open}
        lead={drawer.lead}
        startVisitLog={drawer.open && drawer.visitLog}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        onExited={() => setDrawer({ lead: null, open: false, visitLog: false })}
        onUpdated={(next) => setDrawer((d) => (d.lead && d.lead.id === next.id ? { ...d, lead: { ...d.lead, ...next } } : d))}
        onDeleted={() => setDrawer((d) => ({ ...d, open: false }))}
        mobileFullScreen
        closeOnOutsideClick
      />
    </Box>
  );
}
